import DateTimePickerModal from "@/src/components/action-modals/date-time-modal";
import MapPreview, {
  EventCoordinates,
} from "@/src/components/events/event-form/map-preview";
import ScButton from "@/src/components/ui-kit/button";
import InputField from "@/src/components/ui-kit/input-field.rhf";
import { AttachmentItem, Step1FormValues } from "@/src/models/event.model";
import { useTheme } from "@/src/styles/theme-provider";
import { forwardGeocode } from "@/src/utils/location.utils";
import { pickFile, pickMedia } from "@/src/utils/picker-utils";
import {
  formatDisplayDate,
  formatDisplayTime,
  toHHmm,
} from "@/src/utils/string.utils";
import {
  AttachCircle,
  CalendarEdit,
  CloseCircle,
  Image,
} from "iconsax-react-nativejs";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Pressable,
  Image as RNImage,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Toast from "react-native-toast-message";
import { FileAttachment } from "../../posts/post/file-attachment";
import CategorySelector from "../../ui-kit/category-selector/category-selector";
import ScToggle from "../../ui-kit/toggle";

type Step1DetailsProps = Readonly<{
  defaultValues: Step1FormValues | null;
  existingCoverUrl?: string | null;
  existingAttachments?: string[];
  onSubmit: (data: Step1FormValues) => void;
  onRemoveExistingCover?: () => void;
  onCancel: () => void;
}>;

const Step1Details = ({
  defaultValues,
  existingCoverUrl,
  existingAttachments = [],
  onRemoveExistingCover,
  onSubmit,
  onCancel,
}: Step1DetailsProps) => {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [mapCoords, setMapCoords] = useState<EventCoordinates | undefined>();

  const [remainingExistingAttachments, setRemainingExistingAttachments] =
    useState<string[]>(existingAttachments);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<Step1FormValues>({
    defaultValues: {
      title: "",
      description: "",
      singleDay: false,
      startDate: "",
      endDate: "",
      startTime: "",
      endTime: "",
      locationName: "Thimphu, Bhutan",
      locationLat: 27.4728,
      locationLng: 89.6393,
      categoryIds: [],
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  useEffect(() => {
    setRemainingExistingAttachments(existingAttachments);
  }, [existingAttachments]);

  const singleDay = watch("singleDay");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const startTime = watch("startTime");
  const endTime = watch("endTime");
  const coverImage = watch("coverImage");
  const locationName = watch("locationName");
  const attachments = watch("attachments") ?? [];

  const applyCoords = (coords: EventCoordinates) => {
    setMapCoords(coords);
    setValue("locationLat", coords.lat);
    setValue("locationLng", coords.lng);
  };

  const handlePickImage = async () => {
    const images = await pickMedia(false, "images");
    if (images) {
      setValue("coverImage", images[0]);
    } else {
      Toast.show({ type: "error", text1: t("ERROR_NO_IMAGES_SELECTED") });
    }
  };
  const handlePickAttachments = async () => {
    const files = await pickFile(true);
    if (files && !files.canceled) {
      setValue("attachments", [...attachments, ...files.assets]);
    }
  };

  const handleRemoveNewAttachment = (index: number) => {
    setValue(
      "attachments",
      attachments.filter((_, attachmentIndex) => attachmentIndex !== index),
    );
  };

  const handleRemoveExistingAttachment = (url: string) => {
    setRemainingExistingAttachments((prev) =>
      prev.filter((existingUrl) => existingUrl !== url),
    );
  };

  const handleLocationInput = async (text: string) => {
    if (text.length < 3) return;
    const coords = await forwardGeocode(text);
    if (coords) applyCoords(coords);
  };

  const handleSubmitWithExisting = (data: Step1FormValues) => {
    onSubmit({ ...data, existingAttachments: remainingExistingAttachments });
  };

  const combinedAttachments: AttachmentItem[] = [
    ...remainingExistingAttachments.map((url) => ({
      type: "existing" as const,
      url,
    })),
    ...attachments.map((file) => ({
      type: "new" as const,
      file,
    })),
  ];

  return (
    <View>
      <InputField
        showLabel={true}
        name="title"
        label={t("LABEL_EVENT_TITLE")}
        control={control}
        rules={{
          required: { value: true, message: t("ERROR_TITLE_REQUIRED") },
        }}
        keyboardType="default"
        backgroundColor="bg-chalk"
        placeholderColor="text-onyx"
        placeholder={t("HINT_ENTER_EVENTS_TITLE")}
        error={errors.title?.message}
      />

      <InputField
        showLabel={true}
        name="description"
        label={t("LABEL_DESCRIPTION")}
        control={control}
        keyboardType="default"
        backgroundColor="bg-obsidian"
        placeholder={t("HINT_ENTER_EVENT_DESCRIPTION")}
        multiline
        numberOfLines={3}
      />

      <View className="mt-1 mb-4">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_SINGLE_DAY_EVENT")}
        </Text>
        <View className="flex-row items-center justify-between rounded-xl px-4 py-3 md:border md:border-ash-3">
          <Text className="text-sm text-ash-2 flex-1 pr-4">
            {t("HINT_SINGLE_DAY_EVENT")}
          </Text>
          <Controller
            control={control}
            name="singleDay"
            render={({ field }) => (
              <ScToggle value={field.value} onChange={field.onChange} />
            )}
          />
        </View>
      </View>

      <View className="mb-4">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_DATE_AND_TIME")}
        </Text>
        <Controller
          control={control}
          name="startDate"
          rules={{
            required: {
              value: true,
              message: t("ERROR_DATE_AND_TIME_REQUIRED"),
            },
          }}
          render={() => (
            <Pressable
              onPress={() => setDatePickerOpen(true)}
              className={`flex-row items-center gap-3 bg-chalk rounded-xl px-4 py-3 border hover:bg-ash-3 ${
                errors.startDate ? "border-red-600" : "border-ash-3"
              }`}
            >
              <View className="flex-1">
                {startDate ? (
                  <>
                    <View className="flex-row items-center gap-2 flex-wrap">
                      <Text className="text-sm font-semibold text-onyx">
                        {formatDisplayDate(startDate)}
                      </Text>
                      {!singleDay && endDate && (
                        <>
                          <Text className="text-sm text-ash-2">→</Text>
                          <Text className="text-sm font-semibold text-onyx">
                            {formatDisplayDate(endDate)}
                          </Text>
                        </>
                      )}
                    </View>
                    {startTime && endTime && (
                      <View className="flex-row items-center gap-2 mt-0.5">
                        <Text className="text-xs text-ash-2">
                          {formatDisplayTime(startTime)}
                        </Text>
                        <Text className="text-xs text-ash-2">→</Text>
                        <Text className="text-xs text-ash-2">
                          {formatDisplayTime(endTime)}
                        </Text>
                      </View>
                    )}
                  </>
                ) : (
                  <Text className="text-sm text-gray-dark">
                    {t("HINT_SELECT_DATE_AND_TIME")}
                  </Text>
                )}
              </View>
              <CalendarEdit
                size={20}
                color={themeVars["--color-primary-default"] as string}
              />
              {startDate && (
                <Text className="text-xs text-primary font-medium">
                  {t("LABEL_EDIT")}
                </Text>
              )}
            </Pressable>
          )}
        />
        {errors.startDate && (
          <Text className="text-red-600 text-xs mt-1">
            {errors.startDate.message as string}
          </Text>
        )}
      </View>

      <View className="mb-4">
        <Controller
          control={control}
          name="categoryIds"
          rules={{
            validate: (val) =>
              (val && val.length > 0) || t("ERROR_CATEGORY_REQUIRED"),
          }}
          render={({ field }) => {
            return (
              <>
                <CategorySelector
                  entityType="events"
                  selectedIds={field.value ?? []}
                  onSelectedIdsChange={field.onChange}
                />
                {errors.categoryIds && (
                  <Text className="text-red-600 text-xs mt-1">
                    {errors.categoryIds.message as string}
                  </Text>
                )}
              </>
            );
          }}
        />
      </View>

      <View className="mb-4">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_LOCATION")}
        </Text>
        <MapPreview
          location={locationName}
          coordinates={mapCoords}
          onLocationSelect={(name, coords) => {
            setValue("locationName", name, { shouldValidate: true });
            applyCoords(coords);
          }}
        />
        <InputField
          showLabel={false}
          name="locationName"
          control={control}
          rules={{
            required: { value: true, message: t("ERROR_LOCATION_REQUIRED") },
          }}
          keyboardType="default"
          backgroundColor="bg-chalk"
          borderRadius="rounded-b-lg"
          placeholderColor="text-onyx"
          placeholder={t("HINT_SEARCH_LOCATION")}
          error={errors.locationName?.message}
          onChangeText={handleLocationInput}
        />
      </View>

      <View className="mb-6">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_COVER_IMAGE")}
        </Text>
        <TouchableOpacity
          onPress={handlePickImage}
          className={`${coverImage || existingCoverUrl ? "py-2" : "py-6"} border-2 border-dashed border-ash-3 rounded-lg p-3 items-center justify-center bg-chalk hover:bg-ash-3`}
        >
          {coverImage || existingCoverUrl ? (
            <>
              <RNImage
                source={{
                  uri: coverImage ? coverImage.uri : existingCoverUrl!,
                }}
                style={{ width: "100%", height: 160, borderRadius: 8 }}
                resizeMode="contain"
              />
              <TouchableOpacity
                onPress={(e) => {
                  e.stopPropagation();
                  setValue("coverImage", null);
                  if (!coverImage) onRemoveExistingCover?.();
                }}
                hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
                className="rounded-full absolute top-4 right-4 hover:bg-ash-3"
              >
                <CloseCircle
                  size={24}
                  color={themeVars["--color-ash-1"] as string}
                  variant="Bold"
                />
              </TouchableOpacity>
            </>
          ) : (
            <View className="items-center">
              <View className="w-12 h-12 bg-gray-light rounded-full items-center justify-center mb-2">
                <Image size={32} color={themeVars["--color-primary-default"] as string} />
              </View>
              <Text className="text-primary font-medium text-sm mb-1">
                {t("LABEL_UPLOAD_IMAGE")}
              </Text>
              <Text className="text-gray-light-2 text-xs">
                {t("LABEL_JPG_PNG_UPTO_20MB")}
              </Text>
            </View>
          )}
        </TouchableOpacity>
      </View>

      <View className="mb-6">
        <Text className="font-medium mb-2 text-onyx">
          {t("LABEL_ATTACHMENTS")}
        </Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View className="flex-row gap-2 mb-2">
            {combinedAttachments.map((item) => (
              <FileAttachment
                key={item.type === "existing" ? item.url : item.file.uri}
                file={{
                  url: item.type === "existing" ? item.url : item.file.uri,
                  file_name:
                    item.type === "existing" ? undefined : item.file.name,
                  file_size:
                    item.type === "existing" ? null : (item.file.size ?? 0),
                }}
                onRemove={() => {
                  if (item.type === "existing") {
                    handleRemoveExistingAttachment(item.url);
                  } else {
                    handleRemoveNewAttachment(
                      attachments.findIndex(
                        (file) => file.uri === item.file.uri,
                      ),
                    );
                  }
                }}
              />
            ))}
          </View>
        </ScrollView>

        <TouchableOpacity
          onPress={handlePickAttachments}
          className="flex-row items-center border-2 border-dashed border-ash-3 rounded-lg p-3 gap-2 self-start hover:bg-ash-3"
          activeOpacity={0.7}
        >
          <AttachCircle size={20} color={themeVars["--color-ash-1"] as string} />
          <Text className="text-ash-2 font-medium text-sm">
            {t("LABEL_ADD_ATTACHMENTS")}
          </Text>
        </TouchableOpacity>
      </View>

      <View className="flex-row justify-between">
        <ScButton text={t("BTN_CANCEL")} mode="outlined" onPress={onCancel} />
        <View className="flex-row gap-4">
          <ScButton text={t("BTN_PREVIOUS")} mode="outlined" disabled />
          <ScButton
            text={t("BTN_CONTINUE")}
            onPress={handleSubmit(handleSubmitWithExisting)}
          />
        </View>
      </View>

      <DateTimePickerModal
        visible={datePickerOpen}
        variant="date-time"
        singleDay={singleDay}
        initialValues={{
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          startTime: toHHmm(startTime) || undefined,
          endTime: toHHmm(endTime) || undefined,
        }}
        onClose={() => setDatePickerOpen(false)}
        onApply={(result) => {
          setValue("startDate", result.startDate ?? "", {
            shouldValidate: true,
          });
          setValue("endDate", result.endDate ?? "");
          setValue("startTime", result.startTime);
          setValue("endTime", result.endTime);
          setDatePickerOpen(false);
        }}
      />
    </View>
  );
};

export default Step1Details;
